import React, { useRef, useEffect, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import { useContainerMeasurement } from '../hooks/useContainerMeasurement';
import { usePragmaticDrag } from '../hooks/usePragmaticDrag';
import { useSelectionAutoScroll } from '../hooks/useSelectionAutoScroll';
import { useSelectionKeyboard } from '../hooks/useSelectionKeyboard';
import {
  constrainSelectionTime,
  timeToAbsolutePixel,
} from '../utils/selectionCalculations';
import { VideoSelection } from '../utils/videoUtils';
import SelectionBorder from './SelectionBorder';
import SelectionLabel from './SelectionLabel';

interface SelectionIndicatorProps {
  selection: VideoSelection;
  videoDuration: number;
  stripWidth: number;
  stripHeight: number;
  isScrollable: boolean;
  onSelectionMove: (startTime: number) => void;
  className?: string;
  onVideoTimeUpdate?: (time: number) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  onPlayPause?: () => void;
  instanceId: symbol;
  isPlaying?: boolean;
}

export default function SelectionIndicator({
  selection,
  videoDuration,
  stripWidth,
  stripHeight,
  isScrollable,
  onSelectionMove,
  className = '',
  onVideoTimeUpdate,
  scrollContainerRef,
  onPlayPause,
  instanceId,
  isPlaying = false,
}: SelectionIndicatorProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const { containerWidth } = useContainerMeasurement(scrollContainerRef);

  const { ensureSelectionVisible } = useSelectionAutoScroll(
    scrollContainerRef,
    isScrollable
  );

  // Calculate selection width
  const selectionWidth = timeToAbsolutePixel(
    selection.duration,
    videoDuration,
    stripWidth
  );

  // Viewport info for Pragmatic drag calculations
  const viewport = {
    containerWidth,
    stripWidth,
    videoDuration,
  };

  const handleEnsureSelectionVisible = useCallback(
    (overrideStartTime?: number) => {
      ensureSelectionVisible(
        selection,
        videoDuration,
        stripWidth,
        selectionWidth,
        containerWidth,
        overrideStartTime
      );
    },
    [
      ensureSelectionVisible,
      selection,
      videoDuration,
      stripWidth,
      selectionWidth,
      containerWidth,
    ]
  );

  const constrainedOnSelectionMove = useCallback(
    (startTime: number) => {
      const constrainedStartTime = constrainSelectionTime(
        startTime,
        selection.duration,
        videoDuration
      );
      onSelectionMove?.(constrainedStartTime);
    },
    [onSelectionMove, videoDuration, selection.duration]
  );

  // Keyboard navigation hook - needs to be before drag hook to get the focus handler
  const { isKeyboardFocused, handleKeyDown, handleFocus, handleBlur } =
    useSelectionKeyboard(
      videoDuration,
      constrainedOnSelectionMove,
      onVideoTimeUpdate,
      onPlayPause,
      handleEnsureSelectionVisible,
      containerRef
    );

  const clearKeyboardFocus = useCallback(() => {
    handleBlur();
  }, [handleBlur]);

  const { isDragging, elementRef, updateElementData } = usePragmaticDrag({
    onSelectionMove: constrainedOnSelectionMove,
    onVideoTimeUpdate,
    onClearKeyboardFocus: clearKeyboardFocus,
    instanceId,
    scrollContainerRef,
    isPlaying,
    onPlayPause,
  });

  // Update element data for Pragmatic drag calculations
  useEffect(() => {
    updateElementData(selection, viewport);
  }, [updateElementData, selection, viewport]);

  // Calculate absolute positioning within container
  const constrainedStartTime = constrainSelectionTime(
    selection.startTime,
    selection.duration,
    videoDuration
  );

  const absoluteContentPosition = timeToAbsolutePixel(
    constrainedStartTime,
    videoDuration,
    stripWidth
  );

  // Direct DOM position updates on manual scroll
  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current;
    const element = elementRef.current;
    if (!scrollContainer || !element) return;

    const handleScroll = () => {
      const currentScrollLeft = scrollContainer.scrollLeft;
      const newLeft = absoluteContentPosition - currentScrollLeft;
      element.style.left = `${newLeft}px`;
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [absoluteContentPosition, scrollContainerRef]);

  // Initial position calculation for render
  const currentScrollLeft = scrollContainerRef?.current?.scrollLeft || 0;
  const selectionLeft = absoluteContentPosition - currentScrollLeft;

  const selectionLabelBuffer = 40; //pixels

  return (
    <div
      className={twMerge(
        'absolute pointer-events-none top-0 left-0 w-full z-10',
        className
      )}
    >
      <div
        className="relative overflow-hidden"
        ref={containerRef}
        style={{
          height: stripHeight + selectionLabelBuffer,
          width: containerWidth,
        }}
      >
        {/* Pragmatic Drag and Drop Selection Component */}
        <div
          ref={elementRef}
          className={`absolute pointer-events-auto outline-none selection-handle top-0 ${
            isKeyboardFocused ? 'ring-2 ring-white ring-opacity-80' : ''
          }`}
          style={{
            left: `${selectionLeft}px`,
            width: `${selectionWidth}px`,
            height: `${stripHeight}px`,
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          onKeyDown={(e) => handleKeyDown(e, selection)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          tabIndex={0}
          role="slider"
          aria-label={`Video selection indicator. Duration: ${
            selection.duration
          } seconds. Start time: ${selection.startTime.toFixed(
            1
          )} seconds. Use arrow keys to move, Enter or Space to play/pause, Backspace to reset.`}
          aria-valuemin={0}
          aria-valuemax={videoDuration}
          aria-valuenow={selection.startTime}
          aria-valuetext={`Selection at ${selection.startTime.toFixed(
            1
          )} seconds, duration ${selection.duration} seconds`}
        >
          <SelectionBorder
            isDragging={isDragging}
            isKeyboardFocused={isKeyboardFocused}
          />
          <SelectionLabel isDragging={isDragging} />
        </div>
      </div>
    </div>
  );
}
